import {useHotkeys} from '@mantine/hooks';
import {RefObject} from 'react';

/**
 * Registers an Escape-key shortcut that invokes `onClose`, but only when it is safe to dismiss a
 * full-window preview:
 *  - no modal is open (`[aria-modal="true"]`) — a modal should handle Escape itself,
 *  - focus is not captured by an unrelated element outside `containerRef` (e.g. AI Chat, a
 *    split-window entry view),
 *  - the event has not already been handled (`defaultPrevented`).
 *
 * Shared by the two notebook preview surfaces that can be dismissed with Escape: image/file
 * previews (rendered by ExternalFilePreview) and chart/embedding previews (NoteLinkedEmbeddingPreview).
 *
 * `useHotkeys` listens on `document.documentElement`. We pass an empty `tagsToIgnore` list and
 * `triggerOnContentEditable` so the guards below — not Mantine's built-in target filtering — decide
 * when to close, and `{preventDefault: false}` so we only consume the event when we actually close.
 *
 * When `onClose` is undefined the shortcut is inert and never consumes the Escape key, so previews
 * that aren't dismissible (e.g. those rendered without an `onClose`) are unaffected.
 */
export default function useEscapeToClosePreview(
  onClose: (() => void) | undefined,
  containerRef: RefObject<HTMLElement>
): void {
  useHotkeys(
    [
      [
        'Escape',
        (e: KeyboardEvent): void => {
          if (!onClose || e.defaultPrevented || document.querySelector('[aria-modal="true"]')) {
            return;
          }
          const active = document.activeElement;
          if (
            active &&
            active !== document.body &&
            active !== document.documentElement &&
            !containerRef.current?.contains(active)
          ) {
            return;
          }
          e.preventDefault();
          e.stopPropagation();
          onClose();
        },
        {preventDefault: false},
      ],
    ],
    [],
    true
  );
}
